import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ASSETS } from "@/lib/assets";

const ease = [0.76, 0, 0.24, 1];

export const SpaceChapter = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y1 = useTransform(scrollYProgress, [0, 1], ["6%", "-10%"]);
    const y2 = useTransform(scrollYProgress, [0, 1], ["-4%", "8%"]);

    return (
        <section
            id="space"
            ref={ref}
            className="py-20 md:py-40 px-5 md:px-10 relative overflow-hidden"
            data-testid="space-section"
        >
            <div className="mx-auto max-w-[1600px]">
                <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-20">
                    <div className="col-span-12 md:col-span-2">
                        <div className="label text-surya-muted">Chapter</div>
                        <div className="chapter-num text-[3rem] md:text-[4.5rem] leading-none text-surya-ink mt-1">
                            03<span className="text-surya-orange">.</span>
                        </div>
                        <div className="label text-surya-muted">The Space</div>
                    </div>
                    <div className="col-span-12 md:col-span-8 md:col-start-4">
                        <h2 className="font-serif text-surya-ink text-[clamp(2rem,4.5vw,4rem)] leading-[0.95] tracking-[-0.02em] font-light">
                            A room designed to{" "}
                            <span className="italic-display text-surya-orange">
                                slow your pulse.
                            </span>
                        </h2>
                        <p className="mt-5 md:mt-6 text-surya-muted text-sm md:text-base max-w-xl leading-relaxed">
                            Clean lines, warm wood, sanitised light. Modern
                            equipment held to hospital-grade sterilisation
                            protocols — because comfort begins with confidence.
                        </p>
                    </div>
                </div>

                {/* Asymmetric gallery */}
                <div className="grid grid-cols-12 gap-6 md:gap-8 items-start">
                    <motion.div
                        style={{ y: y1 }}
                        className="col-span-12 md:col-span-7 md:col-start-1"
                    >
                        <motion.div
                            initial={{ clipPath: "inset(12% 15% 12% 15%)", scale: 1.08 }}
                            whileInView={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{ duration: 1.3, ease }}
                            className="relative aspect-[4/3] overflow-hidden bg-surya-surface border border-surya-line"
                        >
                            <img
                                src={ASSETS.operatory}
                                alt="Operatory at Surya Dental Care"
                                className="editorial-img h-full w-full object-cover"
                                data-testid="space-gallery-image-1"
                                loading="lazy"
                                decoding="async"
                            />
                        </motion.div>
                        <div className="mt-3 flex justify-between label text-surya-muted text-[0.6rem] md:text-xs">
                            <span>Fig. 02 — Suite B</span>
                            <span>Wood · Ivory · Ember</span>
                        </div>
                    </motion.div>

                    <div className="col-span-12 md:col-span-4 md:col-start-9 md:mt-20 space-y-6 md:space-y-8">
                        <motion.div style={{ y: y2 }}>
                            <motion.div
                                initial={{ clipPath: "inset(12% 15% 12% 15%)", scale: 1.08 }}
                                whileInView={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
                                viewport={{ once: true, margin: "-10%" }}
                                transition={{ duration: 1.3, ease }}
                                className="relative aspect-[3/4] overflow-hidden bg-surya-surface border border-surya-line"
                            >
                                <img
                                    src={ASSETS.chair}
                                    alt="Dental chair detail"
                                    className="editorial-img h-full w-full object-cover"
                                    data-testid="space-gallery-image-2"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </motion.div>
                            <div className="mt-3 flex justify-between label text-surya-muted text-[0.6rem] md:text-xs">
                                <span>Fig. 03 — Detail</span>
                                <span>Suite A · 2026</span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{ duration: 1, ease }}
                            className="border-t border-surya-line pt-5 md:pt-6"
                        >
                            <div className="label text-surya-orange mb-2 md:mb-3">
                                Sterilisation
                            </div>
                            <p className="font-serif text-surya-ink text-2xl md:text-3xl font-light leading-tight">
                                Autoclave · <span className="italic-display">UV</span> · Single-use
                            </p>
                            <p className="mt-3 text-xs md:text-sm text-surya-muted">
                                Every instrument, every visit. No shortcuts.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
